import React, { useEffect, useRef, useState, useCallback } from 'react';
import { onSnapshot, doc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast, { Toaster } from 'react-hot-toast';
import '../styles/rewards.css';
import { getBadgeIcon } from '../utils/badges';
import { useAuth } from '../context/AuthContext';

function TrophyIcon({ size = 24 }) {
  return (
    <img
      src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f3c6.svg"
      alt="trophy"
      width={size}
      height={size}
      className="reward-trophy-icon"
    />
  );
}

function XpToast({ amount, message }) {
  return (
    <div className="xp-toast">
      <div className="xp-toast-icon">⭐</div>
      <div className="xp-toast-body">
        <div className="xp-toast-title">+{amount} XP</div>
        {message ? <div className="xp-toast-message">{message}</div> : null}
      </div>
    </div>
  );
}

function BadgeModal({ open, onClose, badge }) {
  if (!open || !badge) return null;
  const title = badge.title || null;
  const lines = Array.isArray(badge.lines) ? badge.lines : [];
  const ctas = Array.isArray(badge.ctas) ? badge.ctas : [];
  const name = badge.name || badge.title || 'New Badge';
  const tierValue = badge.level != null ? badge.level + 1 : (badge.tier != null ? badge.tier : null);
  const tier = tierValue ? `Tier ${tierValue}` : '';
  const iconURL = getBadgeIcon(badge);
  const xp = Number(badge.xpReward ?? badge.xpAwarded ?? badge.xp ?? 0);
  return (
    <div className="reward-modal-overlay" role="dialog" aria-modal="true">
      <div className="reward-modal">
        <button className="reward-modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="reward-modal-hero">
          <img src={iconURL} alt="badge" className="reward-badge-icon" />
        </div>
        <div className="reward-modal-title">
          {title ? (<>{title}</>) : (<><TrophyIcon size={20} />&nbsp;{name} {tier}</>)}
        </div>
        {xp > 0 ? <div className="reward-modal-xp">+{xp} XP</div> : null}
        {lines.length > 0 && (
          <div className="reward-modal-lines">
            {lines.map((l, i) => (<div key={i}>{l}</div>))}
          </div>
        )}
        <div className="reward-modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Awesome!</button>
          {ctas.map((c, i) => (
            <a key={i} className="btn btn-secondary" href={c.href || '#'} onClick={c.onClick || undefined}>{c.label || 'Open'}</a>
          ))}
          {!ctas.length ? <a className="btn btn-secondary" href="/trophy-room">View Trophy Room</a> : null}
        </div>
      </div>
    </div>
  );
}

export default function RewardProvider({ children }) {
  const { user, userDoc } = useAuth();
  const [pendingBadge, setPendingBadge] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const lastXpRef = useRef(null);
  const firstStatsSnapRef = useRef(true);
  const badgeQueueRef = useRef([]);
  const showingRef = useRef(false);

  useEffect(() => {
    // Reset reward state when user changes.
    firstStatsSnapRef.current = true;
    lastXpRef.current = null;
    badgeQueueRef.current = [];
    showingRef.current = false;
    setModalOpen(false);
    setPendingBadge(null);
  }, [user?.uid]);

  // Listen for XP increases on users/{uid}
  useEffect(() => {
    if (!user) return;
    const stats = userDoc?.stats || {};
    const currentXp = Number(stats.xp || 0);
    if (firstStatsSnapRef.current) {
      firstStatsSnapRef.current = false;
      lastXpRef.current = currentXp;
      return;
    }
    const prev = Number(lastXpRef.current || 0);
    if (currentXp > prev) {
      const delta = currentXp - prev;
      toast.custom(<XpToast amount={delta} message={null} />, { duration: 3500 });
    }
    lastXpRef.current = currentXp;
  }, [user, userDoc]);

  const showNextBadge = useCallback(async () => {
    if (showingRef.current) return;
    const next = badgeQueueRef.current.shift();
    if (!next) return;
    showingRef.current = true;
    setPendingBadge(next);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(async () => {
    setModalOpen(false);
    const b = pendingBadge;
    setPendingBadge(null);
    showingRef.current = false;
    // Mark seen when possible
    try {
      if (user && b && b.id) {
        await updateDoc(doc(db, 'users', user.uid, 'badges', b.id), { seen: true });
      }
    } catch (err) {
      console.error('RewardProvider load failed', err);
    }
    // Show next, if queued
    setTimeout(() => showNextBadge(), 300);
  }, [pendingBadge, showNextBadge, user]);

  // Listen for unseen badges in users/{uid}/badges and queue modals
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users', user.uid, 'badges'), where('seen', '==', false));
    const unsub = onSnapshot(q, (snap) => {
      const newly = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Merge into queue without duplicates; cap at 20 to prevent unbounded growth
      const MAX_BADGE_QUEUE = 20;
      const existingIds = new Set(badgeQueueRef.current.map(b => b.id));
      newly.forEach(b => {
        if (!existingIds.has(b.id) && badgeQueueRef.current.length < MAX_BADGE_QUEUE) {
          badgeQueueRef.current.push(b);
        }
      });
      // Start showing if idle
      if (!showingRef.current) showNextBadge();
    });
    return () => unsub();
  }, [user, showNextBadge]);

  // Fallback for legacy extraBadges (admin-granted)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', user.uid, 'extraBadges'));
        if (!snap.empty) {
          toast((t) => (
            <div className="xp-toast">
              <div className="xp-toast-icon"><TrophyIcon /></div>
              <div className="xp-toast-body">
                <div className="xp-toast-title">New badges available</div>
                <div className="xp-toast-message">Check your Trophy Room</div>
              </div>
            </div>
          ), { duration: 4000 });
        }
      } catch (err) {
        console.error('RewardProvider cache failed', err);
      }
    })();
  }, [user]);

  // Expose combined modal API
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__REWARD__ = {
        showCombined: ({ title, xp, badge, lines, ctas }) => {
          try {
            const payload = {
              title: title || null,
              xp: Number(xp || 0),
              name: badge?.name || badge?.label || null,
              level: typeof badge?.level === 'number' ? badge.level : null,
              tier: typeof badge?.tier === 'number' ? badge.tier : null,
              iconURL: badge?.iconURL || badge?.icon || 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/2728.svg',
              lines: Array.isArray(lines) ? lines : [],
              ctas: Array.isArray(ctas) ? ctas : []
            };
            // Open immediately as a modal; do not add an id so it won't toggle seen
            if (!showingRef.current) {
              showingRef.current = true;
              setPendingBadge(payload);
              setModalOpen(true);
            } else {
              badgeQueueRef.current.push(payload);
            }
          } catch (err) {
            console.error('RewardProvider badge update failed', err);
          }
        }
      };
    }
  }, []);

  return (
    <>
      {children}
      <Toaster position="bottom-right" />
      <BadgeModal open={modalOpen} onClose={closeModal} badge={pendingBadge} />
    </>
  );
} 
